/* eslint-disable */
import React from "react";
import {
  Card,
  Text,
  Grid,
  Box,
  Group,
  Stack,
  ThemeIcon,
  Center,
  Loader,
} from "@mantine/core";
import {
  IconMap,
  IconCalendar,
  IconClock,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { useGetUserProfile } from "api/users/getUserProfile";
import { User } from "types";
import PunchCard from "./PunchCard";
import {
  DashboardStatistic,
  useGetDashboardStatistics,
} from "api/attendance/dashboardStatistics";
import { UseQueryResult } from "@tanstack/react-query";
import TimelineChart from "./TimeLineChart";
import { useGetTotalHour } from "api/attendance/getUserWorkingHours";

interface StatsCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  trend: string;
  trendType: "positive" | "negative";
  bgColor: string;
  valueSize?: "xs" | "sm" | "md" | "lg" | "xl"; // Add this
  valueWeight?: number; // Optional: font weight
}

const StatsCard: React.FC<StatsCardProps> = ({
  icon,
  value,
  label,
  trend,
  trendType,
  bgColor,
  valueSize = "xl", // Default value
  valueWeight = 700,
}) => {
  const trendColor = trendType === "positive" ? "green" : "red";

  return (
    <Card shadow="sm" radius="md" withBorder>
      <Stack spacing="xs">
        <ThemeIcon color={bgColor as any} variant="filled" size="lg">
          {icon}
        </ThemeIcon>
        <Text fw={valueWeight} size={valueSize}>
          {value}
        </Text>
        <Text size="xs" color="dimmed">
          {label}
        </Text>
        <Group spacing={4}>
          <Box
            sx={(theme) => ({
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor:
                trendType === "positive"
                  ? theme.colors.green[6]
                  : theme.colors.red[6],
            })}
          />
          <Text size="xs" color={trendColor}>
            {trend}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
};

interface EmployeeAttendanceDetailProps {
  userId?: string | null;
}

const EmployeeAttendanceDetail: React.FC<EmployeeAttendanceDetailProps> = ({ userId }) => {
  const { userid } = useParams();

  // Use userId prop if provided, otherwise use URL param (for backward compatibility)
  const targetUserId = userId || userid;

  const { data: user } = useGetUserProfile(targetUserId);

  const typedUser = user as User | null;
  const now = new Date();
  const { data: payload, isLoading } =
    useGetDashboardStatistics(targetUserId) as UseQueryResult<DashboardStatistic, unknown>;
  const formattedDateTime = now.toLocaleString("en-GB", {
    // hour: "2-digit",
    // minute: "2-digit",
    // hour12: true,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const {
    data: workingHours,
    isLoading: hoursLoading,
    isError,
  } = useGetTotalHour(targetUserId);

  const expectedDaily = workingHours?.totalHours ?? 0;
  const todayVal = Number(payload?.today?.totalWorkingHours ?? 0);
  const weekVal = Number(payload?.thisWeek?.totalWorkingHours ?? 0);
  const monthVal = Number(payload?.thisMonth?.totalWorkingHours ?? 0);
  const totalWeekVal = Number(payload?.thisWeek?.totalWeekHours ?? 0);
  const totalMonthVal = Number(payload?.thisMonth?.totalMonthlyHours ?? 0);

  const overtimeVal = Number(payload?.overtime?.hours ?? 0);
  const overtimeMax = Number(payload?.overtime?.maxHours ?? 0);

  const pctOf = (value: number, max: number) =>
    max > 0 ? Math.min(100, (value / max) * 100) : 0;

  const todayPct = pctOf(todayVal, expectedDaily);
  const weekPct = pctOf(weekVal, expectedDaily * 5);
  const monthPct = pctOf(monthVal, expectedDaily * 22);
  const overtimePct = pctOf(
    overtimeVal,
    overtimeMax || Math.max(1, overtimeVal)
  );
  if (isLoading) {
    return (
      <Center style={{ height: 200 }}>
        <Loader />
      </Center>
    );
  }
  return (
    <Box
      sx={(theme) => ({
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[7]
            : theme.colors.gray[0],
        //padding: theme.spacing.md,
        width: "100%",
        marginBottom: "10px",
      })}
    >
      <Box
        sx={{
          maxWidth: "100%", // remove fixed max width
          width: "100%",
          margin: "0 auto",
        }}
      >
        <Grid gutter="lg" align="stretch">
          {/* Left Side (Punch Card) */}
          <Grid.Col xs={12} md={4} lg={3}>
            <PunchCard
              firstName={typedUser?.firstName}
              lastName={typedUser?.lastName}
              dateTime={formattedDateTime}
              productionHours={`${payload?.today?.totalWorkingHours.toFixed(
                2
              )}h`}
              // clockInTime={userStats?.clockIn}
            />
          </Grid.Col>

          {/* Right Side (Stats + Chart) */}
          <Grid.Col xs={12} md={8} lg={9}>
            <Stack spacing="lg">
              {/* Stats Section */}
              <Grid gutter="md" align="stretch">
                <Grid.Col xs={6} sm={6} md={3}>
                  <StatsCard
                    icon={<IconMap size={18} />}
                    value={`${payload?.today?.totalWorkingHours.toFixed(
                      2
                    )}h / ${workingHours?.totalHours?.toFixed(2)}h`}
                    label="Total Hours Today"
                    trend={`${Math.round(todayPct)}% Today`}
                    trendType={todayPct >= 50 ? "positive" : "negative"}
                    bgColor="grape"
                    valueSize="xl"
                    valueWeight={600}
                  />
                </Grid.Col>
                <Grid.Col xs={6} sm={6} md={3}>
                  <StatsCard
                    icon={<IconCalendar size={18} />}
                    value={`${payload?.thisWeek?.totalWorkingHours.toFixed(
                      2
                    )}h / ${totalWeekVal.toFixed(2)}h`}
                    label="Total Hours Week"
                    trend={`${Math.round(weekPct)}% This Week`}
                    trendType={weekPct >= 50 ? "positive" : "negative"}
                    bgColor="dark"
                    valueSize="xl"
                    valueWeight={600}
                  />
                </Grid.Col>
                <Grid.Col xs={6} sm={6} md={3}>
                  <StatsCard
                    icon={<IconClock size={18} />}
                    value={`${payload?.thisMonth?.totalWorkingHours.toFixed(
                      2
                    )}h / ${totalMonthVal.toFixed(2)}h`}
                    label="Total Hours Month"
                    trend={`${Math.round(monthPct)}% This Month`}
                    trendType={monthPct >= 50 ? "positive" : "negative"}
                    bgColor="blue"
                    valueSize="xl"
                    valueWeight={600}
                  />
                </Grid.Col>
                <Grid.Col xs={6} sm={6} md={3}>
                  <StatsCard
                    icon={<IconAlertCircle size={18} />}
                    value={`${payload?.overtime?.hours.toFixed(
                      2
                    )}h / ${payload?.overtime?.maxHours.toFixed(2)}h`}
                    label="Overtime this Month"
                    trend={`${Math.round(overtimePct)}% OverTime`}
                    trendType={overtimePct >= 50 ? "positive" : "negative"}
                    bgColor="pink"
                    valueSize="xl"
                    valueWeight={600}
                  />
                </Grid.Col>
              </Grid>

              {/* Timeline Chart */}
              <TimelineChart
                totalWorkingHours={`${workingHours?.totalHours?.toFixed(2)}h`}
                productiveHours={`${payload?.today?.totalWorkingHours?.toFixed(
                  2
                )}h`}
                breakHours={`${payload?.today?.breakHours?.toFixed(2)}h`}
                overtimeHours={`${payload?.today?.overtimeCount?.toFixed(2)}h`}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Box>
    </Box>
  );
};

export default EmployeeAttendanceDetail;
